import { Container, Box, useMediaQuery, useTheme } from '@mui/material';
import { motion, AnimatePresence } from 'framer-motion';
import AuthSidePanel from './components/AuthSidePanel';
import AuthFormContainer from './components/AuthFormContainer';

const AuthLayout = ({ 
  sideImageUrl, 
  FormComponent, 
  pageTitle, 
  pageDescription,
  additionalLink,
  sideTitle,
  sideSubtitle
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  return (
    <Box
      sx={{
        minHeight: '100vh',
        background: theme.palette.gradient.primary,
        position: 'relative',
        overflow: 'hidden',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      }}
    >
      {/* Floating Particles */}
      <Box
        sx={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: `
            radial-gradient(circle at 20% 80%, rgba(0, 255, 136, 0.1) 0%, transparent 50%),
            radial-gradient(circle at 80% 20%, rgba(0, 153, 255, 0.1) 0%, transparent 50%),
            radial-gradient(circle at 40% 40%, rgba(255, 51, 102, 0.05) 0%, transparent 50%)
          `,
          zIndex: 1,
        }}
      />

      <Container 
        maxWidth={false} 
        sx={{
          minHeight: '100vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          p: 0,
          position: 'relative',
          zIndex: 2,
        }}
      >
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.8, ease: "easeOut" }}
          style={{ 
            width: '100%',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
          }}
        >
          <Box 
            sx={{
              width: '100%',
              maxWidth: { xs: '100%', md: 1100, lg: 1200 },
              background: 'linear-gradient(135deg, rgba(255, 255, 255, 0.1) 0%, rgba(255, 255, 255, 0.05) 100%)',
              backdropFilter: 'blur(20px)',
              border: '1px solid rgba(255, 255, 255, 0.1)',
              borderRadius: { xs: 0, md: '24px' },
              overflow: 'hidden',
              display: 'flex',
              height: { xs: '100vh', md: '100%' },
              flexDirection: { xs: 'column', md: 'row' },
              boxShadow: '0 25px 50px rgba(0, 0, 0, 0.5)',
              position: 'relative',
              '&::before': {
                content: '""',
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                height: '3px',
                background: 'linear-gradient(90deg, #00ff88, #0099ff, #ff3366)',
                zIndex: 1,
              },
            }}
          >
            <AnimatePresence mode="wait">
              {!isMobile && (
                <motion.div
                  key="side-panel"
                  initial={{ opacity: 0, x: -50 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: -50 }}
                  transition={{ duration: 0.6 }}
                  style={{ flex: 1, display: 'flex' }}
                >
                  <AuthSidePanel 
                    imageUrl={sideImageUrl} 
                    title={sideTitle} 
                    subtitle={sideSubtitle} 
                  />
                </motion.div>
              )}
            </AnimatePresence>
            
            <motion.div
              key="form-container"
              initial={{ opacity: 0, x: isMobile ? 0 : 50 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6, delay: 0.2 }}
              style={{ flex: 1, display: 'flex' }}
            >
              <AuthFormContainer 
                FormComponent={FormComponent}
                pageTitle={pageTitle}
                pageDescription={pageDescription}
                additionalLink={additionalLink}
                isMobile={isMobile}
              />
            </motion.div>
          </Box>
        </motion.div>
      </Container>
    </Box>
  );
};

export default AuthLayout;